// Firestore-backed mock API — keeps the same shape (axios-like get/post/put/delete)
// so existing page components don't need to change.
import {
  collection, doc, getDoc, getDocs, query, where, addDoc, setDoc, serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { auth } from "../firebase/config";
import {
  listProducts, getProductBySlug, listAll, getArtistBySlug,
  getCategoryBySlug,
} from "../services/firestore";
import { getOrderSettings, generateTrackingNo } from "../services/orderSettings";

const SESSION_KEY = "vbaz_session_id";
export function getSessionId() {
  let s = localStorage.getItem(SESSION_KEY);
  if (!s) {
    s = (typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID()) ||
      "s_" + Math.random().toString(36).slice(2);
    localStorage.setItem(SESSION_KEY, s);
  }
  return s;
}

const ok = (data) => Promise.resolve({ data, status: 200 });
const err = (status, detail) => Promise.reject({ response: { status, data: { detail } }, message: detail });

const CARTS_KEY = "vbaz_carts_store";
const readJSON = (k, fb) => { try { return JSON.parse(localStorage.getItem(k) || "null") ?? fb; } catch { return fb; } };
const writeJSON = (k, v) => localStorage.setItem(k, JSON.stringify(v));
const allCarts = () => readJSON(CARTS_KEY, {});
const saveCart = (sid, cart) => { const c = allCarts(); c[sid] = cart; writeJSON(CARTS_KEY, c); };

function parseQS(path) {
  const [base, qs] = path.split("?");
  return [base, new URLSearchParams(qs || "")];
}

async function handleGet(path) {
  const [base, qs] = parseQS(path);

  if (base === "/products") {
    const items = await listProducts({
      q: qs.get("q") || undefined,
      category_id: qs.get("category_id") || undefined,
      artist_id: qs.get("artist_id") || undefined,
      featured: qs.get("featured") === "true" ? true : qs.get("featured") === "false" ? false : undefined,
      new_arrival: qs.get("new_arrival") === "true" ? true : qs.get("new_arrival") === "false" ? false : undefined,
      lim: qs.get("limit") ? parseInt(qs.get("limit"), 10) : undefined,
    });
    return ok(items);
  }
  if (base.startsWith("/products/")) {
    const slug = base.slice("/products/".length);
    const data = await getProductBySlug(slug);
    if (!data) return err(404, "Product not found");
    return ok(data);
  }
  if (base === "/artists") return ok(await listAll("artists"));
  if (base.startsWith("/artists/")) {
    const data = await getArtistBySlug(base.slice("/artists/".length));
    if (!data) return err(404, "Artist not found");
    return ok(data);
  }
  if (base === "/categories") return ok(await listAll("categories", { sort: "order", dir: "asc" }));
  if (base.startsWith("/categories/")) {
    const data = await getCategoryBySlug(base.slice("/categories/".length));
    if (!data) return err(404, "Category not found");
    return ok(data);
  }
  if (base === "/delivery-methods") {
    const list = await listAll("delivery_methods");
    return ok(list.filter((d) => d.active !== false));
  }
  if (base === "/currency/rates") return ok({ AZN: 1, USD: 0.59, EUR: 0.54 });
  if (base === "/geo") return ok({ currency: "AZN", rates: { AZN: 1, USD: 0.59, EUR: 0.54 } });
  if (base.startsWith("/cart/")) {
    const sid = base.slice("/cart/".length);
    return ok(allCarts()[sid] || { session_id: sid, items: [] });
  }
  if (base.startsWith("/orders/track/")) {
    const tno = base.slice("/orders/track/".length);
    const snap = await getDocs(query(collection(db, "orders"), where("tracking_no", "==", tno)));
    if (snap.empty) return err(404, "Order not found");
    const d = snap.docs[0];
    return ok({ id: d.id, ...d.data() });
  }
  if (base === "/orders/mine") {
    const uid = auth.currentUser?.uid;
    if (!uid) return ok([]); // not signed in → no orders
    // Query Firestore for orders belonging to this user; we filter unpaid
    // checkouts (`awaiting_payment`, `payment_failed`) out so only orders
    // where the bank actually charged the card are listed.
    const snap = await getDocs(query(collection(db, "orders"), where("user_id", "==", uid)));
    const HIDDEN = new Set(["awaiting_payment", "payment_failed"]);
    const list = snap.docs
      .map((d) => {
        const data = d.data();
        // Convert Firestore Timestamp to ISO so the UI can format it.
        const ts = data.created_at;
        const created_at = ts && typeof ts.toDate === "function" ? ts.toDate().toISOString() : data.created_at;
        return { id: d.id, ...data, created_at };
      })
      .filter((o) => !HIDDEN.has(o.status))
      .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    return ok(list);
  }
  return err(404, `No handler for GET ${path}`);
}

async function handlePost(path, body) {
  if (path === "/cart") {
    const sid = body?.session_id;
    if (!sid) return err(400, "session_id required");
    const allProducts = await listAll("products");
    const allArtists = await listAll("artists");
    const items = (body.items || []).map((i) => {
      // Preserve gift-card line items as-is (they don't correspond to a real product).
      if (i.is_gift_card) return { ...i };
      const p = allProducts.find((x) => x.id === i.product_id);
      const a = p ? allArtists.find((x) => x.id === p.artist_id) : null;
      return p ? {
        product_id: p.id, title: p.title, artist_name: a?.name || "",
        cover_url: p.cover_url, price_azn: p.price_azn, qty: i.qty,
      } : i;
    });
    const cart = { session_id: sid, items, updated_at: new Date().toISOString() };
    saveCart(sid, cart);
    return ok(cart);
  }

  if (path === "/orders") {
    // Admin paneldən redaktə edilə bilən prefiksdən istifadə edirik
    // (default "VAZ-2026"). Bax: services/orderSettings.js.
    const settings = await getOrderSettings();
    const tracking_no = generateTrackingNo(settings?.tracking_prefix);
    const allProducts = await listAll("products");
    const allArtists = await listAll("artists");
    const items = (body.items || []).map((i) => {
      if (i.is_gift_card) {
        // Pass through gift-card line items intact.
        return { ...i, qty: i.qty || 1 };
      }
      const p = allProducts.find((x) => x.id === i.product_id);
      const a = p ? allArtists.find((x) => x.id === p.artist_id) : null;
      return {
        product_id: i.product_id, title: i.title || p?.title || "",
        artist_name: i.artist_name || a?.name || "", cover_url: i.cover_url || p?.cover_url || "",
        price_azn: i.price_azn ?? p?.price_azn ?? 0, qty: i.qty,
      };
    });
    const subtotal = items.reduce((s, i) => s + i.price_azn * i.qty, 0);
    const discount = Number(body.discount_azn) || 0;
    const total_azn = Math.max(0, subtotal - discount) + (body.delivery_price_azn || 0);
    const initialStatus = body.initial_status || "placed";
    const order = {
      tracking_no, items,
      delivery_code: body.delivery_code || "standard",
      delivery_price_azn: body.delivery_price_azn || 0,
      full_name: body.full_name || "", phone: body.phone || "",
      address: body.address || "", city: body.city || "", notes: body.notes || "",
      currency: body.currency || "AZN",
      user_id: body.user_id || null,
      coupon_code: body.coupon_code || null,
      discount_azn: discount,
      subtotal_azn: subtotal, total_azn,
      status: initialStatus,
      status_history: [{ status: initialStatus, ts: new Date().toISOString() }],
      created_at: serverTimestamp(),
    };
    const ref = await addDoc(collection(db, "orders"), order);
    return ok({ id: ref.id, ...order, created_at: new Date().toISOString() });
  }

  return err(404, `No handler for POST ${path}`);
}

export const api = {
  get: handleGet,
  post: handlePost,
  put: () => err(501, "use Firestore service directly"),
  delete: () => err(501, "use Firestore service directly"),
};
